/**
 * Environment utilities for Stripe configuration (CRA-compatible only)
 * Never throws on missing values; always provide safe defaults.
 */

/**
 * Checks if a Stripe publishable key is a test key
 * @param pk - The publishable key to check (pk_live_... or pk_test_...)
 * @returns true if the key starts with 'pk_test_'
 */
export const isTestStripePk = (pk?: string) => !!pk && pk.startsWith('pk_test_');

export const stripeEnvironment = (pk?: string) => (isTestStripePk(pk) ? 'test' : 'live');

// Paywall configuration
export type PaywallMode =
  | 'BUY_BUTTONS'
  | 'PAYMENT_LINKS'
  | 'CHECKOUT_SESSIONS'
  | 'EMBEDDED_CHECKOUT';

// Read env from Vite (import.meta.env) first, then fallback to process.env (CRA)
const read = (keys: string[] | string, defaultValue = ''): string => {
  const list = Array.isArray(keys) ? keys : [keys];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const viteEnv: any | undefined =
    typeof import.meta !== 'undefined' ? (import.meta as any).env : undefined;
  for (const k of list) {
    // Vite style first (VITE_*)
    const vFromVite = viteEnv?.[k];
    if (typeof vFromVite === 'string' && vFromVite.length > 0) return vFromVite as string;
    // CRA style fallback (REACT_APP_*) or generic
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const vFromProcess = (
      typeof process !== 'undefined' ? (process.env as any)?.[k] : undefined
    ) as string | undefined;
    if (typeof vFromProcess === 'string' && vFromProcess.length > 0) return vFromProcess;
  }
  return defaultValue;
};

// Default to PAYMENT_LINKS unless explicitly overridden
// Direct access for CRA production builds (webpack replaces at build time)
export const PAYWALL_MODE: PaywallMode = (process.env.REACT_APP_PAYWALL_MODE ||
  (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_PAYWALL_MODE) ||
  'PAYMENT_LINKS') as PaywallMode;

// Stripe Publishable Key
// Direct access for CRA production builds (webpack replaces at build time)
export const STRIPE_PUBLISHABLE_KEY =
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
  (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_STRIPE_PUBLISHABLE_KEY) ||
  '';

// Payment Links (when using PAYMENT_LINKS)
// Direct access for CRA production builds (webpack replaces at build time)
export const STRIPE_LINK_MONTHLY =
  process.env.REACT_APP_LINK_MONTHLY ||
  (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_LINK_MONTHLY) ||
  '';
export const STRIPE_LINK_ANNUAL =
  process.env.REACT_APP_LINK_ANNUAL ||
  (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_LINK_ANNUAL) ||
  '';
export const STRIPE_LINK_ARCHIVE =
  process.env.REACT_APP_LINK_ARCHIVE ||
  (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_LINK_ARCHIVE) ||
  '';

// Buy Button IDs (when using BUY_BUTTONS)
export const STRIPE_BUY_BTN_MONTHLY = read(
  ['VITE_BUY_BTN_MONTHLY', 'REACT_APP_BUY_BTN_MONTHLY'],
  ''
);
export const STRIPE_BUY_BTN_ANNUAL = read(['VITE_BUY_BTN_ANNUAL', 'REACT_APP_BUY_BTN_ANNUAL'], '');
export const STRIPE_BUY_BTN_ARCHIVE = read(
  ['VITE_BUY_BTN_ARCHIVE', 'REACT_APP_BUY_BTN_ARCHIVE'],
  ''
);

// Admin bootstrap token (dev-only helper; optional)
export const ADMIN_BOOTSTRAP_TOKEN_DEV = read(
  ['VITE_ADMIN_BOOTSTRAP_TOKEN', 'REACT_APP_ADMIN_BOOTSTRAP_TOKEN'],
  ''
);
export const ADMIN_BOOTSTRAP_TOKEN = ADMIN_BOOTSTRAP_TOKEN_DEV;

// Helper booleans – but never throw if empty
export const IS_TEST_PK = isTestStripePk(STRIPE_PUBLISHABLE_KEY);

// Admin email (align with backend cfg.ADMIN_EMAIL)
export const ADMIN_EMAIL = read(
  ['VITE_ADMIN_EMAIL', 'REACT_APP_ADMIN_EMAIL'],
  '1992ignacio@gmail.com'
);
